import SyncIcon from "@mui/icons-material/Sync";
import { FC, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  AccordionHeader,
  AccordionHeaderText,
  MuiBox,
  MuiTypography,
} from "../../../../../../components";
import {
  StatusBadge,
  StatusBadgeType,
} from "../../../../../../components/statusBadge/StatusBadge";
import useAccordion from "../../../../../../hooks/useAccordion";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../../redux/slices/appState/appStateSlice";

import { useGetClientByIdMutation } from "../../../../../../api/services/client/clientApis";
import { ProjectStatus } from "../../../../../../api/services/project/model";
import { useGetUsersDetailByRoleMutation } from "../../../../../../api/services/user/userApi";
import { ButtonWithIcon } from "../../../../../../components/buttonWithIcon/ButtonWithIcon";
import { ExpandCollapseButton } from "../../../../../../components/expandCollapseButton/ExpandCollapseButton";
import PermissionWrapper from "../../../../../../components/permissionWrapper/PermissionWrapper";
import {
  MODULE_PERMISSIONS,
  ROLE_IDS,
} from "../../../../../../helpers/constants";
import { isNotEmpty } from "../../../../../../helpers/utils";
import {
  keyDetailsSelector,
  projectStatusSelector,
} from "../../../../../../redux/slices/project/projectSelector";
import {
  ProjectTabFormKey,
  ProjectTabSectionKey,
} from "../../../../modelsAndSchemas/Models";
import {
  activityTimelineSiteCoreKeys,
  officeAndContactDetailsSiteCoreKeys,
  projectStatusChangeSiteCoreKeys,
  projectStatusSiteCoreKeys,
  salesCreditSiteCoreKeys,
} from "../../../../siteCore/SiteCoreKeys";
import { ProjectActivityAndDeliverableMain } from "./components/activitiesAndDeliverables/ProjectActivityAndDeliverableMain";
import { activitiesListSiteCoreKeys } from "./components/activitiesAndDeliverables/siteCore/SiteCoreKeys";
import ActivityTimeline from "./components/activityTimeline/ActivityTimeline";
import { ChangeProjectStatus } from "./components/changeProjectStatus/ChangeProjectStatus";
import { Facilitator } from "./components/facilitator/Facilitator";
import { FacilitatorDetails } from "./components/facilitator/model";
import { facilitatorSiteCoreKeys } from "./components/facilitator/SiteCoreKeys";
import { ProjectOfficeAndContactDetails } from "./components/projectOfficeAndContactDetail/ProjectOfficeAndContactDetail";
import { ProjectStatusResult } from "./components/projectStatus/ProjectStatusResult";
import { ProjectSalesCreditDetails } from "./components/salesCredit/SalesCreditDetails";

interface ProjectTabProps {
  pageId: string;
  isEditProject: boolean;
  projectId: string;
  projectStatusList?: ProjectStatus[];
  onRefetchPrimaryDetails?: () => void;
  isSaveProjectApiCalledOnce?: boolean;
}

const initialAccordionStatus = {
  all: true,
  officeAndContactDetails: true,
  projectStatus: false,
  salesCredit: false,
  facilitator: false,
  activityTimeline: false,
  activitiesAndDeliverables: false,
};

export const ProjectTab: FC<ProjectTabProps> = ({
  pageId,
  isEditProject,
  projectId,
  projectStatusList,
  onRefetchPrimaryDetails,
  isSaveProjectApiCalledOnce,
}) => {
  const projectKeyDetails = useSelector(keyDetailsSelector);
  const projectStatus = useSelector(projectStatusSelector);
  const clientId = projectKeyDetails?.client?.id as string;
  const dispatch = useDispatch();
  const [showChangeStatusModal, setShowChangeStatusModal] = useState(false);
  const {
    mutateAsync: getClientRelationshipOwners,
    data: clientRelationshipOwnerList,
  } = useGetUsersDetailByRoleMutation();

  const {
    fetchComponentTitle,
    commonLabels,
    fetchLabel,
    fetchValueByKeyOfDropdown,
  } = useSiteCore();
  const {
    accordionStatus,
    handleIndividualAccordionChange,
    handleExpandCollapseAllChange,
    handleAccordionExpansionState,
  } = useAccordion(initialAccordionStatus);

  const {
    formState: { errors },
    setValue,
    getValues,
    watch,
  } = useFormContext();

  useEffect(() => {
    if (isNotEmpty(clientId) && !isEditProject) {
      getClientInfo(clientId);
    }
  }, [clientId]);

  const {
    mutateAsync: getClientInfo,
    data: clientDetails,
    isPending: isClientDetailsPending,
    isError: isClientDetailsError,
  } = useGetClientByIdMutation();

  useEffect(() => {
    getClientRelationshipOwners({ roleIds: [ROLE_IDS.sales] });
  }, []);

  useEffect(() => {
    if (
      isNotEmpty(clientDetails) &&
      isNotEmpty(clientRelationshipOwnerList) &&
      !isSaveProjectApiCalledOnce &&
      !isEditProject
    ) {
      const localClientRelationshipOwnerId =
        clientDetails.data.profile.localClientRelationshipOwnerId;
      const getSalesCreditProjectData = clientRelationshipOwnerList?.find(
        (salesCreditObj) =>
          salesCreditObj.id === localClientRelationshipOwnerId,
      );

      if (getSalesCreditProjectData) {
        const { firstName, lastName, email, officeName, id } =
          getSalesCreditProjectData || {};

        const data = {
          localClientRelationshipOwnerId,
          firstName,
          lastName,
          email,
          percentage: 100,
          officeName,
        };
        // here set sales credit details and client relationship owner once get program details load for create flow
        setValue(ProjectTabFormKey.salesCreditDetails, [data]);
        setValue(ProjectTabFormKey.clientRelationShipOwner, id);
      }
    }
  }, [
    clientDetails,
    clientRelationshipOwnerList,
    isSaveProjectApiCalledOnce,
    isEditProject,
  ]);

  useEffect(() => {
    if (isClientDetailsError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isClientDetailsError]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isClientDetailsPending,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isClientDetailsPending]);

  const renderHeader = (labelKey: string) => {
    const label = fetchComponentTitle(labelKey);
    return <AccordionHeaderText label={label || labelKey} />;
  };

  //allow to edit field based on status
  const sectionFieldsMap: Record<ProjectTabSectionKey, string[]> = {
    officeAndContactDetails: [
      ProjectTabFormKey.billingOffice,
      ProjectTabFormKey.salesOfficeId,
      ProjectTabFormKey.clientRelationShipOwner,
      ProjectTabFormKey.billingContact,
      ProjectTabFormKey.hrContact,
      ProjectTabFormKey.projectManager,
      ProjectTabFormKey.projectCoordinator,
    ],
  };

  const hasErrors = (section: ProjectTabSectionKey) => {
    const fields = sectionFieldsMap[section];
    return fields.some((field) => errors[field]);
  };

  useEffect(() => {
    (Object.keys(sectionFieldsMap) as ProjectTabSectionKey[]).forEach(
      (sectionKey: ProjectTabSectionKey) => {
        if (hasErrors(sectionKey)) {
          handleAccordionExpansionState(sectionKey.toString(), true);
        }
      },
    );
  }, [errors]);

  /**
   * Returns the correct permission based on edit mode and provided permissions.
   * @param isEdit - Boolean indicating if it's edit mode.
   * @param addPermission - Permission string for add mode.
   * @param editPermission - Permission string for edit mode.
   * @returns The appropriate permission string or undefined.
   */
  const resolvePermission = (
    isEdit: boolean,
    addPermission: string,
    editPermission: string,
  ): string | undefined => {
    if (isEdit) return editPermission;
    if (!isEdit) return addPermission;
    return undefined;
  };

  const [projectFacilitatorList, setProjectFacilitatorList] = useState(
    getValues().projectFacilitatorList,
  );

  const projectFacilitator = watch("projectFacilitator");

  useEffect(() => {
    // Ensure facilitator.facilitatorId is unique and not empty
    if (!Array.isArray(projectFacilitator)) {
      setProjectFacilitatorList([]);
      return;
    }

    const seen = new Set<string>();
    const facilitatorList = projectFacilitator
      .reduce((acc: FacilitatorDetails[], facilitator: FacilitatorDetails) => {
        if (facilitator.facilitatorId && !seen.has(facilitator.facilitatorId)) {
          seen.add(facilitator.facilitatorId);
          acc.push(facilitator);
        }
        return acc;
      }, [])
      .map((facilitator: FacilitatorDetails) => ({
        id: facilitator.facilitatorId,
        firstName: facilitator.facilitatorName,
        lastName: "",
        rightOffice: "",
      }));

    setProjectFacilitatorList(facilitatorList);
  }, [projectFacilitator]);

  return (
    <>
      <ExpandCollapseButton
        accordionStatus={accordionStatus}
        handleExpandCollapseAllChange={handleExpandCollapseAllChange}
      />
      <MuiBox className="accordion-header office-contact-header">
        <PermissionWrapper
          permission={resolvePermission(
            isEditProject,
            MODULE_PERMISSIONS.PROJECT_ADD_OFFICE_AND_CONTACT_DETAILS,
            MODULE_PERMISSIONS.PROJECT_EDIT_OFFICE_AND_CONTACT_DETAILS,
          )}
        >
          <AccordionHeader
            title={renderHeader(
              officeAndContactDetailsSiteCoreKeys.officeAndContactDetails,
            )}
            onChange={() =>
              handleIndividualAccordionChange("officeAndContactDetails")
            }
            isExpanded={accordionStatus.officeAndContactDetails}
          >
            <ProjectOfficeAndContactDetails pageId={pageId} />
          </AccordionHeader>
        </PermissionWrapper>
      </MuiBox>
      <MuiBox className="accordion-header">
        <PermissionWrapper
          permission={resolvePermission(
            isEditProject,
            MODULE_PERMISSIONS.PROJECT_ADD_PROJECT_STATUS,
            MODULE_PERMISSIONS.PROJECT_EDIT_PROJECT_STATUS,
          )}
        >
          <AccordionHeader
            title={renderHeader(projectStatusSiteCoreKeys.projectStatus)}
            onChange={() => handleIndividualAccordionChange("projectStatus")}
            isExpanded={accordionStatus.projectStatus}
          >
            <MuiBox className="status-label">
              <MuiTypography variant="h3" display="block">
                {fetchLabel(
                  projectStatusSiteCoreKeys.projectStatus,
                  projectStatusSiteCoreKeys.currentStatus,
                )}
              </MuiTypography>
              <MuiBox className="active-change-status-project">
                <StatusBadge
                  status={projectStatus as StatusBadgeType}
                  label={fetchValueByKeyOfDropdown(
                    projectStatusSiteCoreKeys.projectStatus,
                    projectStatusSiteCoreKeys.statusList,
                    projectStatus,
                  )}
                />
                {isEditProject && (
                  <ButtonWithIcon
                    buttonText={fetchComponentTitle(
                      projectStatusChangeSiteCoreKeys.header,
                    )}
                    ActionIcon={SyncIcon}
                    data-testid={`${pageId}-change-status`}
                    onActionClick={() => setShowChangeStatusModal(true)}
                  />
                )}
              </MuiBox>

              <MuiBox className="project-status-list">
                <ProjectStatusResult
                  rows={projectStatusList ?? []}
                  pageId={pageId}
                />
              </MuiBox>
            </MuiBox>
          </AccordionHeader>
        </PermissionWrapper>
      </MuiBox>
      <MuiBox className="accordion-header">
        <PermissionWrapper
          permission={resolvePermission(
            isEditProject,
            MODULE_PERMISSIONS.PROJECT_ADD_SALES_CREDIT,
            MODULE_PERMISSIONS.PROJECT_EDIT_SALES_CREDIT,
          )}
        >
          <AccordionHeader
            title={renderHeader(salesCreditSiteCoreKeys.salesCredit)}
            onChange={() => handleIndividualAccordionChange("salesCredit")}
            isExpanded={accordionStatus.salesCredit}
          >
            <ProjectSalesCreditDetails
              pageId={pageId}
              isInViewMode={false}
              clientRelationshipOwnerList={clientRelationshipOwnerList ?? []}
              resultsFormKey={ProjectTabFormKey.salesCreditDetails}
              isEditProject={isEditProject}
            />
          </AccordionHeader>
        </PermissionWrapper>
      </MuiBox>

      <MuiBox className="accordion-header">
        <PermissionWrapper
          permission={resolvePermission(
            isEditProject,
            MODULE_PERMISSIONS.PROJECT_ADD_FACILITATOR,
            MODULE_PERMISSIONS.PROJECT_EDIT_FACILITATOR,
          )}
        >
          <AccordionHeader
            title={renderHeader(facilitatorSiteCoreKeys.facilitator)}
            onChange={() => handleIndividualAccordionChange("facilitator")}
            isExpanded={accordionStatus.facilitator}
          >
            <Facilitator pageId={pageId} />
          </AccordionHeader>
        </PermissionWrapper>
      </MuiBox>

      <MuiBox className="accordion-header">
        <PermissionWrapper
          permission={resolvePermission(
            isEditProject,
            MODULE_PERMISSIONS.PROJECT_ADD_ACTIVITIES_AND_DELIVERABLES,
            MODULE_PERMISSIONS.PROJECT_EDIT_ACTIVITIES_AND_DELIVERABLES,
          )}
        >
          <AccordionHeader
            title={renderHeader(activitiesListSiteCoreKeys.activitiesList)}
            onChange={() =>
              handleIndividualAccordionChange("activitiesAndDeliverables")
            }
            isExpanded={accordionStatus.activitiesAndDeliverables}
          >
            <ProjectActivityAndDeliverableMain />
          </AccordionHeader>
        </PermissionWrapper>
      </MuiBox>
      <MuiBox className="accordion-header">
        <PermissionWrapper
          permission={resolvePermission(
            isEditProject,
            MODULE_PERMISSIONS.PROJECT_ADD_ACTIVITY_TIMELINE,
            MODULE_PERMISSIONS.PROJECT_EDIT_ACTIVITY_TIMELINE,
          )}
        >
          <AccordionHeader
            title={renderHeader(activityTimelineSiteCoreKeys.activityTimeline)}
            onChange={() => handleIndividualAccordionChange("activityTimeline")}
            isExpanded={accordionStatus.activityTimeline}
          >
            <ActivityTimeline
              pageId={pageId}
              projectId={projectId}
              projectCoachList={projectFacilitatorList || []}
            />
          </AccordionHeader>
        </PermissionWrapper>
      </MuiBox>

      {showChangeStatusModal && (
        <ChangeProjectStatus
          pageId={pageId}
          open={showChangeStatusModal}
          setShowChangeStatusModal={setShowChangeStatusModal}
          projectId={projectId}
          refetchPrimaryDetails={onRefetchPrimaryDetails!}
        />
      )}
    </>
  );
};
